import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { CacheKeys } from '@/lib/cache'
import {
  dismissFileWarnings,
  fillApplicationErrorMessage,
  type FillApplicationState,
} from '@/models/fillApplication'

/** Clears the files' warnings at once; the server keeps them hidden across reloads, and for teammates. */
export function useDismissFileWarnings(orgId: string): () => void {
  const queryClient = useQueryClient()
  const queryKey = [CacheKeys.FILL_APPLICATION_STATE, orgId]
  const { mutate } = useMutation({
    mutationFn: () => dismissFileWarnings(orgId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<FillApplicationState>(queryKey)
      if (previous?.filesSummary) {
        queryClient.setQueryData<FillApplicationState>(queryKey, {
          ...previous,
          filesSummary: { ...previous.filesSummary, warnings: [] },
        })
      }
      return { previous }
    },
    onSuccess: (state) => queryClient.setQueryData(queryKey, state),
    onError: (error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous)
      toast.error(fillApplicationErrorMessage(error))
    },
  })
  return () => mutate()
}
